import { LocalStorageKey, SessionStorageKey } from 'src/types'

/**
 * Reads the active org ID from browser storage, validated against the user's
 * membership org IDs. Returns `undefined` if no valid org is found in storage.
 *
 * Resolution order:
 * 1. **sessionStorage** (per-tab) -- each browser tab tracks its own active org,
 *    so a user can have different orgs open in different tabs simultaneously.
 * 2. **localStorage** (cross-tab) -- stores the last org the user explicitly
 *    switched to. Used as the default when opening a new tab that has no
 *    sessionStorage value yet.
 *
 * Both values are written by `switchActiveOrg` (explicit user switch) and by
 * `AppStateContextLayout` (when navigating to an explicit-org page like
 * /record/{uuid} that belongs to a different org).
 *
 * This is a plain function (not a hook) so that AppStateContextLayout can call
 * it to resolve the org ID before AppStateContext is available.
 */
export const getActiveOrgIdFromStorage = (membershipOrgIds: Set<number>): number | undefined => {
  const sessionVal = sessionStorage.getItem(SessionStorageKey.ActiveOrgId)
  const sessionOrgId = sessionVal ? Number(sessionVal) : undefined
  if (sessionOrgId && membershipOrgIds.has(sessionOrgId)) {
    return sessionOrgId
  }

  const localVal = localStorage.getItem(LocalStorageKey.LastSelectedOrgId)
  const localOrgId = localVal ? Number(localVal) : undefined
  if (localOrgId && membershipOrgIds.has(localOrgId)) {
    return localOrgId
  }

  return undefined
}

/**
 * Writes the selected org to both sessionStorage (per-tab) and localStorage
 * (cross-tab default), then triggers a full page reload to the homepage.
 */
export const switchActiveOrg = (orgId: number): void => {
  sessionStorage.setItem(SessionStorageKey.ActiveOrgId, String(orgId))
  localStorage.setItem(LocalStorageKey.LastSelectedOrgId, String(orgId))
  window.location.assign('/')
}
